/**
 * Composable для работы с непривязанными товарами
 * 
 * Предоставляет функции для получения количества непривязанных товаров
 * и отображения badge в навигации
 */

import { ref, computed } from 'vue';
import consola from 'consola'
import { useAdminSessionManager } from '@features/auth';
import { apiAdminProduct as APIAdminProduct } from '~/fsd-pages/admin/api';

const unassignedCount = ref<number>(0);
const isLoading = ref<boolean>(false);
const lastFetchTime = ref<number>(0);

// Кэширование на 30 секунд
const CACHE_DURATION = 30 * 1000;

export const useUnassignedProducts = () => {
  const adminSessionManager = useAdminSessionManager();

  /**
   * Загрузить количество непривязанных товаров
   * @param forceRefresh - Принудительное обновление (игнорировать кэш)
   */
  const fetchUnassignedCount = async (forceRefresh = false): Promise<number> => {
    // Не запрашиваем admin API без активной админской сессии
    if (!adminSessionManager.hasAdminSession.value) {
      unassignedCount.value = 0;
      lastFetchTime.value = 0;
      return 0;
    }

    const now = Date.now();
    
    // Если данные свежие и не требуется принудительное обновление
    if (!forceRefresh && lastFetchTime.value && (now - lastFetchTime.value) < CACHE_DURATION) {
      return unassignedCount.value;
    }

    isLoading.value = true;

    try {
      const count = await APIAdminProduct.getUnassignedCount();
      unassignedCount.value = count;
      lastFetchTime.value = now;
      isLoading.value = false;
      return count;
    } catch (error: any) {
      // Нормальный кейс после истечения/очистки админской сессии
      if (error?.status === 401 || error?.statusCode === 401) {
        unassignedCount.value = 0;
        lastFetchTime.value = 0;
        isLoading.value = false;
        return 0;
      }
      consola.error('Ошибка получения количества непривязанных товаров:', error);
      isLoading.value = false;
      return unassignedCount.value; // Возвращаем последнее известное значение
    }
  };

  /**
   * Сбросить кэш и обновить данные
   */
  const refreshCount = (): Promise<number> => fetchUnassignedCount(true)

  /**
   * Получить текущее количество (синхронно)
   */
  const getCount = (): number => {
    return unassignedCount.value;
  };

  /**
   * Проверить, есть ли непривязанные товары
   */
  const hasUnassigned = computed(() => unassignedCount.value > 0);

  /**
   * Сбросить счетчик (после привязки всех товаров)
   */
  const resetCount = () => {
    unassignedCount.value = 0;
    lastFetchTime.value = 0;
  };

  return {
    // State
    unassignedCount,
    isLoading,
    hasUnassigned,
    
    // Actions
    fetchUnassignedCount,
    refreshCount,
    getCount,
    resetCount,
  };
};

export default useUnassignedProducts;

